import {
  RequirementType,
  SharedFileRequestContextType
} from "@wingspanhq/wingspan-api-sdk";
import intersection from "lodash/intersection";
import startCase from "lodash/startCase";
import toLower from "lodash/toLower";
import upperFirst from "lodash/upperFirst";

import { selectorIsContextTypesMatched } from "../../Requirements/modules/InsuranceCoverage/selectors/selectorIsContextTypesMatched";
import { TEMPLATES } from "../routes/RouteCreateRequirementByTemplate/templates";
import { RequirementDefinitionTemplate } from "../routes/RouteCreateRequirementByTemplate/types";

const sentenceCase = (str: string) => upperFirst(toLower(startCase(str)));

/**
 * Wire-value normalization: the search rows and older notification payloads
 * still spell the document-upload case with the v1 `SharedFile` where the v3
 * enum says `DocumentUpload`. Every other value matches the v3 spelling.
 */
const normalizeType = (
  type: RequirementType | string | undefined
): RequirementType | undefined =>
  type === "SharedFile"
    ? RequirementType.DOCUMENT_UPLOAD
    : (type as RequirementType | undefined);

/**
 * Params compare by wire value — the search rows, the v3 configuration reads
 * and the v3 runtime reads share the same context/policy-type strings.
 */
export function getRequirementDefinitionTemplateByParams(params: {
  type?: RequirementType | string;
  contextType?: string;
  policyType?: string | string[];
  strict?: boolean;
}): RequirementDefinitionTemplate {
  const type = normalizeType(params.type);
  const policyTypes = params.policyType
    ? Array.isArray(params.policyType)
      ? params.policyType
      : [params.policyType]
    : [];

  let template = TEMPLATES.find(template => {
    const isRequirementTypeMatched = template.requirementType === type;

    const isContextTypeMatched = template.contextType
      ? selectorIsContextTypesMatched(params, template.contextType)
      : true;

    const isPolicyTypeMatched = template.policyType
      ? !!intersection(policyTypes, template.policyType).length
      : true;

    return (
      isRequirementTypeMatched && isContextTypeMatched && isPolicyTypeMatched
    );
  });

  if (params.strict && !template) {
    throw new Error(
      `No template found for ${type} ${
        params.contextType || params.policyType
      }. Please update TEMPLATES constant to support this requirement.`
    );
  }

  if (!template) {
    if (type !== RequirementType.INSURANCE_COVERAGE) {
      // If the type is not insurance coverage, we can try to find a standard template
      template = TEMPLATES.find(template => {
        if (template.requirementType !== type || template.policyType) {
          return false;
        }

        return Array.isArray(template.contextType)
          ? template.contextType.length === 0 ||
              template.contextType.includes(SharedFileRequestContextType.NONE)
          : [undefined, SharedFileRequestContextType.NONE].includes(
              template.contextType
            );
      });

      if (template) {
        return template;
      }
    }

    return {
      costPerSubmission: 0,
      form: null,
      helperText: "Policy type not found",
      icon: "alert-circle",
      image: "",
      isBillable: true,
      isRequirementWithErrorStates: false,
      isSmartAdvanced: true,
      key: "generic",
      modal: {
        cost: "",
        description: "",
        exampleUseCases: ""
      },
      name: `${sentenceCase(type ?? "")} ${
        params.contextType || policyTypes.length
          ? params.contextType || policyTypes.join(", ")
          : ""
      }`.trim(),
      policyType: policyTypes,
      requirementType: RequirementType.INSURANCE_COVERAGE,
      tier: "SmartAdvanced"
    };
  }

  return template;
}
